import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { artifactMentionEntries } from '@/features/artifacts/artifact-mention.js';
import { sharePointMentionEntries } from '@/features/sharepoint/sharepoint-mention.js';
import {
  sharePointSearchQueryOptions,
  type SharePointSearchHit,
} from '@/lib/api/sharepoint.js';
import {
  workspaceArtifactsQueryOptions,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import {
  mentionSections,
  type MentionEntry,
  type MentionSection,
} from './mention-entry.js';

/**
 * How long the typing settles before SharePoint is asked. The search is a call to Microsoft Search, so
 * without this every keystroke is a round trip — the same quarter second the SharePoint browser and the
 * knowledge picker wait.
 */
const SHAREPOINT_SEARCH_DEBOUNCE_MS = 250;

/**
 * How much has to be typed before SharePoint is asked at all. A bare `@` is a user who has not said what
 * they are looking for yet, and answering it would be a search of the whole tenant.
 */
const MIN_SHAREPOINT_QUERY = 2;

export interface MentionEntriesResult {
  /** The rows grouped by source, in the order the sections are declared. */
  sections: MentionSection[];
  /** The same rows flattened in section order — what the arrow keys walk. */
  entries: MentionEntry[];
  /** True while a source may still have rows on the way, so an empty list can say which it is. */
  searching: boolean;
}

/**
 * The rows the composer's `@` popup lists, from every source that has any.
 *
 * The two sources answer on very different clocks, and that is the whole design problem here. The
 * workspace's own files are already on the client, so they appear as the popup opens; SharePoint is a
 * search across a tenant behind a debounce, and lands later. Two things keep the list from moving under
 * the user's Enter as it arrives: a source's rows can only appear inside that source's own section, whose
 * position is fixed by `mentionSections`, and the highlight is tracked by row key rather than by index
 * (see `useComposer`).
 *
 * A source that fails is a source with no rows. A workspace with no SharePoint connection answers 412 to
 * every search — that is a fact about the workspace, not an error the person typing a file name caused,
 * so its section is simply absent and the workspace's own files are listed as usual.
 *
 * The mention token is still being typed, so Microsoft Search gets a trailing wildcard for prefix
 * matching. Without it, a name such as `Nvidia_SOW_` can lose a file that `Nvidia_SOW` finds. Only the
 * remote query changes; artifact matching and the text removed on selection use the original token.
 */
export function useMentionEntries({
  workspaceId,
  query,
  onSelectArtifact,
  onSelectSharePointFile,
}: {
  workspaceId: string | null;
  /** The text typed after the `@`, or `null` while no mention popup is open. */
  query: string | null;
  onSelectArtifact: (artifact: WorkspaceArtifact) => void;
  onSelectSharePointFile: (hit: SharePointSearchHit) => void;
}): MentionEntriesResult {
  const active = query !== null && workspaceId !== null;

  /**
   * The same query the "Add artifacts" dialog and the Artifacts section read, so the list is usually
   * already in cache and the rows are on screen the moment the `@` is typed.
   */
  const artifacts = useQuery({
    ...workspaceArtifactsQueryOptions(workspaceId ?? ''),
    enabled: active,
  });

  const [settledQuery, setSettledQuery] = useState<string | null>(query);
  useEffect(() => {
    if (query === null) {
      setSettledQuery(null);
      return;
    }
    /**
     * Falling back under the minimum settles at once instead of waiting out the debounce. The delay
     * exists to spare Microsoft Search a round trip per keystroke, and there is no round trip to
     * spare here — the search is about to be switched off. Waiting would leave the previous hits on
     * screen for a quarter second after the person deleted what found them, which is long enough for
     * an Enter to attach a file their sentence no longer names.
     */
    if (query.trim().length < MIN_SHAREPOINT_QUERY) {
      setSettledQuery(query);
      return;
    }
    const timer = setTimeout(
      () => setSettledQuery(query),
      SHAREPOINT_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [query]);

  const searchable =
    settledQuery !== null && settledQuery.trim().length >= MIN_SHAREPOINT_QUERY;
  /**
   * One page is all a popup of five rows per source can show, so nothing here asks for a second one. A
   * failed search is not retried either: the popup is live under the typing, and the answer to a search
   * that did not work is the next keystroke.
   */
  const search = useInfiniteQuery({
    ...sharePointSearchQueryOptions(
      workspaceId ?? '',
      /** The workspace's shared connection: a mention popup offers no place to choose one. */
      null,
      searchable ? `${settledQuery}*` : '',
    ),
    enabled: active && searchable,
    retry: false,
  });
  const searchData = search.data;
  const hits = useMemo<SharePointSearchHit[]>(
    () => searchData?.pages[0]?.items ?? [],
    [searchData],
  );

  const sections = useMemo<MentionSection[]>(() => {
    if (!active) return [];
    return mentionSections([
      ...artifactMentionEntries(
        artifacts.data ?? [],
        query ?? '',
        onSelectArtifact,
      ),
      ...sharePointMentionEntries(hits, onSelectSharePointFile),
    ]);
  }, [
    active,
    artifacts.data,
    hits,
    query,
    onSelectArtifact,
    onSelectSharePointFile,
  ]);

  const entries = useMemo<MentionEntry[]>(
    () => sections.flatMap((section) => section.entries),
    [sections],
  );

  const sharePointPending =
    active &&
    (query ?? '').trim().length >= MIN_SHAREPOINT_QUERY &&
    (settledQuery !== query || search.isFetching);

  return {
    sections,
    entries,
    searching: active && (artifacts.isLoading || sharePointPending),
  };
}
